import { addHours, addMinutes, subtractMinutes, isPast, generateId } from './timeHelpers';
import { resolvePatientWardType } from './patientUtils';
import { withRequiresDocumentation } from './taskDocumentation';
import { splitDoseAndRoute, resolveMedicationRoute } from './medicationTaskDose';

function inferShiftAnchorFromTasks(tasks, fallbackShiftStart) {
  const dueTimes = (tasks || [])
    .filter((t) => t.dueTime && t.type !== 'generated')
    .map((t) => new Date(t.dueTime).getTime())
    .filter((ts) => Number.isFinite(ts));

  if (!dueTimes.length) return new Date(fallbackShiftStart);
  return new Date(Math.min(...dueTimes));
}

function getRelativeHourSlot(task, shiftStart) {
  if (!task?.dueTime) return 'ongoing';
  const hours = (new Date(task.dueTime).getTime() - new Date(shiftStart).getTime()) / (60 * 60 * 1000);
  return Math.round(hours * 10) / 10;
}

function getTaskSeedSignature(task, shiftStart) {
  if (task.type === 'fluid_balance') return 'fluid_balance';
  return [
    task.type,
    task.sourceNoteType || '',
    String(task.title || '').toLowerCase().trim(),
    String(normalizeMedicationTaskItemLabel(task) || '').toLowerCase().trim(),
    getRelativeHourSlot(task, shiftStart),
  ].join('::');
}

function tasksEquivalent(existing, template, shiftStart) {
  return getTaskSeedSignature(existing, shiftStart) === getTaskSeedSignature(template, shiftStart);
}

function createVitalsTask(
  patientId,
  dueTime,
  windowMinutesBefore = 30,
  windowMinutesAfter = 30,
  priority = 'normal',
  itemLabel = 'Blood Pressure'
) {
  return {
    id: generateId(),
    patientId,
    type: 'recurring',
    title: 'Vitals',
    itemLabel,
    dueTime: dueTime.toISOString(),
    windowStart: subtractMinutes(dueTime, windowMinutesBefore).toISOString(),
    windowEnd: addMinutes(dueTime, windowMinutesAfter).toISOString(),
    status: isPast(addMinutes(dueTime, windowMinutesAfter)) ? 'overdue' : 'pending',
    completedAt: null,
    completedByNoteId: null,
    completionMode: 'manual',
    sourceNoteType: 'vitals',
    requiresDocumentation: true,
    priority,
  };
}

function createProgressNoteTask(
  patientId,
  dueTime,
  windowMinutesBefore = 30,
  windowMinutesAfter = 60,
  priority = 'normal'
) {
  return {
    id: generateId(),
    patientId,
    type: 'recurring',
    title: 'Progress Note',
    dueTime: dueTime.toISOString(),
    windowStart: subtractMinutes(dueTime, windowMinutesBefore).toISOString(),
    windowEnd: addMinutes(dueTime, windowMinutesAfter).toISOString(),
    status: isPast(addMinutes(dueTime, windowMinutesAfter)) ? 'overdue' : 'pending',
    completedAt: null,
    completedByNoteId: null,
    completionMode: 'manual',
    sourceNoteType: 'progress_note',
    requiresDocumentation: true,
    priority,
  };
}

function formatMedicationItemLabel(med) {
  if (!med) return 'Medication';
  const name = med.name || med.drug_name || 'Medication';
  const dose = capMedicationDoseForTaskLabel(med.dose || '');
  return dose ? `${name} ${dose}` : name;
}

function normalizeMedicationTaskItemLabel(task) {
  if (task?.sourceNoteType !== 'medication_admin' || !task.itemLabel) {
    return task?.itemLabel;
  }
  return capMedicationDoseForTaskLabel(task.itemLabel);
}

function normalizeSeededTask(task) {
  const itemLabel = normalizeMedicationTaskItemLabel(task);
  const next = itemLabel === task?.itemLabel ? task : { ...task, itemLabel };
  return withRequiresDocumentation(next);
}

function capMedicationDoseForTaskLabel(dose) {
  const raw = String(dose || '').trim();
  if (!raw) return '';

  return raw.replace(/([\d.]+)\s*(mg|g)\b/i, (match, amountText, unitText) => {
    const amount = parseFloat(amountText);
    if (!Number.isFinite(amount)) return match;

    const unit = unitText.toLowerCase();
    const amountMg = unit === 'g' ? amount * 1000 : amount;
    if (amountMg <= 500) return match;
    return '500mg';
  });
}

function isPrnFrequency(frequency) {
  return /\bPRN\b/i.test(String(frequency || ''));
}

function hoursFromFrequency(frequency) {
  const freq = String(frequency || '').toUpperCase().replace(/\bPRN\b/g, '').trim();
  if (!freq || freq === 'PRN') return null;
  if (freq.includes('Q4')) return [0, 4, 8, 12];
  if (freq.includes('Q6')) return [0, 6, 12];
  if (freq.includes('Q8')) return [0, 8];
  if (freq.includes('TID')) return [0, 4, 8];
  if (freq.includes('BID')) return [0, 8];
  if (freq.includes('OD') || freq.includes('QD') || freq.includes('DAILY') || freq.includes('WEEKLY')) {
    return [0];
  }
  return null;
}

function createMARTask(
  patientId,
  dueTime,
  windowMinutesBefore = 30,
  windowMinutesAfter = 60,
  priority = 'normal',
  itemLabel = 'Medication',
  options = {}
) {
  const isPrn = Boolean(options.isPrn);
  const dose = options.dose || '';
  const route = resolveMedicationRoute({
    route: options.route || '',
    dose,
    itemLabel,
    name: itemLabel,
    drug_name: itemLabel,
    metadata: options.metadata,
  });
  const metadata = {
    ...(options.metadata || {}),
    ...(route ? { route } : {}),
    ...(dose ? { dose } : {}),
  };
  if (!dueTime) {
    return {
      id: generateId(),
      patientId,
      type: 'recurring',
      title: 'Medications',
      itemLabel,
      dueTime: null,
      windowStart: null,
      windowEnd: null,
      status: 'ongoing',
      completedAt: null,
      completedByNoteId: null,
      completionMode: 'manual',
      sourceNoteType: 'medication_admin',
      source: 'manual',
      requiresDocumentation: true,
      priority,
      isPrn: true,
      route,
      dose,
      metadata,
    };
  }
  return {
    id: generateId(),
    patientId,
    type: 'recurring',
    title: 'Medications',
    itemLabel,
    dueTime: dueTime.toISOString(),
    windowStart: subtractMinutes(dueTime, windowMinutesBefore).toISOString(),
    windowEnd: addMinutes(dueTime, windowMinutesAfter).toISOString(),
    status: isPast(addMinutes(dueTime, windowMinutesAfter)) ? 'overdue' : 'pending',
    completedAt: null,
    completedByNoteId: null,
    completionMode: 'manual',
    sourceNoteType: 'medication_admin',
    source: 'manual',
    requiresDocumentation: true,
    priority,
    isPrn,
    route,
    dose,
    metadata,
  };
}

function seedMedicationTasks(
  patientId,
  medications = [],
  shiftStart,
  fallbackHours = [1, 6, 11],
  windowBefore = 30,
  windowAfter = 60,
  priority = 'normal'
) {
  const tasks = [];
  const meds = Array.isArray(medications) ? medications.filter(Boolean) : [];

  if (!meds.length) {
    fallbackHours.forEach((h) => {
      tasks.push(
        createMARTask(patientId, addHours(shiftStart, h), windowBefore, windowAfter, priority, 'Medication')
      );
    });
    return tasks;
  }

  meds.forEach((med, index) => {
    const label = formatMedicationItemLabel(med);
    const prn = isPrnFrequency(med.frequency);
    const hours = hoursFromFrequency(med.frequency);
    const split = splitDoseAndRoute(med.dose || '');
    const dose = String(med.route ? med.dose || '' : split.dose || med.dose || '').trim();
    const route = resolveMedicationRoute({
      route: med.route || split.route || '',
      dose: med.dose || dose,
      name: med.name || '',
      drug_name: med.name || '',
      itemLabel: label,
    });
    const medOptions = {
      isPrn: prn,
      route,
      dose,
    };

    if (prn && !hours) {
      tasks.push(
        createMARTask(patientId, null, windowBefore, windowAfter, priority, label, medOptions)
      );
      return;
    }

    (hours || [fallbackHours[index % fallbackHours.length]]).forEach((h) => {
      tasks.push(
        createMARTask(
          patientId,
          addHours(shiftStart, h),
          windowBefore,
          windowAfter,
          priority,
          label,
          medOptions
        )
      );
    });
  });

  return tasks;
}

function createHandoverTask(patientId, shiftStart) {
  const dueTime = addHours(shiftStart, 11.5);
  return {
    id: generateId(),
    patientId,
    type: 'recurring',
    title: 'Shift Handover',
    dueTime: dueTime.toISOString(),
    windowStart: addHours(shiftStart, 11).toISOString(),
    windowEnd: addHours(shiftStart, 13).toISOString(),
    status: 'pending',
    completedAt: null,
    completedByNoteId: null,
    completionMode: 'manual',
    sourceNoteType: 'handover',
    requiresDocumentation: false,
    priority: 'high',
  };
}

function createManualTask(patientId, title, hoursFromShiftStart, shiftStart, priority = 'normal') {
  const dueTime = addHours(shiftStart, hoursFromShiftStart);
  return {
    id: generateId(),
    patientId,
    type: 'manual',
    title,
    dueTime: dueTime.toISOString(),
    windowStart: subtractMinutes(dueTime, 30).toISOString(),
    windowEnd: addMinutes(dueTime, 30).toISOString(),
    status: isPast(addMinutes(dueTime, 30)) ? 'overdue' : 'pending',
    completedAt: null,
    completedByNoteId: null,
    completionMode: 'manual',
    sourceNoteType: null,
    priority,
  };
}

function createFluidBalanceTask(patientId, shiftStart) {
  return {
    id: generateId(),
    patientId,
    type: 'fluid_balance',
    title: 'Fluid Balance',
    requiresDocumentation: true,
    status: 'pending',
    fluidSummary: {
      totalInput: 0,
      totalOutput: 0,
      netBalance: 0,
      entryCount: 0,
    },
    shiftDate: shiftStart.toISOString(),
    completedAt: null,
    completionMode: 'manual',
  };
}

function seedAllWardsTasks(patientId, shiftStart, medications = []) {
  const tasks = [];

  const vitalHours = [0, 4, 8];
  vitalHours.forEach((h) => {
    tasks.push(createVitalsTask(patientId, addHours(shiftStart, h), 60, 60));
  });

  const noteHours = [0, 8];
  noteHours.forEach((h) => {
    tasks.push(createProgressNoteTask(patientId, addHours(shiftStart, h), 30, 120));
  });

  tasks.push(...seedMedicationTasks(patientId, medications, shiftStart, [1, 6, 11], 30, 60));
  tasks.push(createHandoverTask(patientId, shiftStart));

  return tasks;
}

function seedICUTasks(patientId, shiftStart, medications = []) {
  const tasks = [];

  for (let i = 0; i <= 12; i += 1) {
    tasks.push(createVitalsTask(patientId, addHours(shiftStart, i), 20, 20, 'high'));
  }

  for (let i = 0; i <= 12; i += 2) {
    tasks.push(createProgressNoteTask(patientId, addHours(shiftStart, i), 30, 30, 'high'));
  }

  for (let i = 0; i <= 12; i += 2) {
    tasks.push(createManualTask(patientId, 'Patient Repositioning', i, shiftStart, 'normal'));
  }

  tasks.push(...seedMedicationTasks(patientId, medications, shiftStart, [0, 4, 8, 12], 20, 30, 'high'));

  tasks.push(createManualTask(patientId, 'Neurological Assessment', 0, shiftStart, 'high'));
  tasks.push(createManualTask(patientId, 'Neurological Assessment', 4, shiftStart, 'high'));
  tasks.push(createManualTask(patientId, 'Neurological Assessment', 8, shiftStart, 'high'));

  tasks.push(createHandoverTask(patientId, shiftStart));

  return tasks;
}

function seedMedicalTasks(patientId, shiftStart, medications = []) {
  const tasks = [];

  const vitalHours = [0, 4, 8];
  vitalHours.forEach((h) => {
    tasks.push(createVitalsTask(patientId, addHours(shiftStart, h), 60, 60));
  });

  const noteHours = [0, 8];
  noteHours.forEach((h) => {
    tasks.push(createProgressNoteTask(patientId, addHours(shiftStart, h), 30, 120));
  });

  tasks.push(...seedMedicationTasks(patientId, medications, shiftStart, [0, 6, 11], 30, 60));

  tasks.push(createManualTask(patientId, 'Blood Sugar Monitoring', 0, shiftStart));
  tasks.push(createManualTask(patientId, 'Blood Sugar Monitoring', 6, shiftStart));

  tasks.push(createHandoverTask(patientId, shiftStart));

  return tasks;
}

function seedSurgicalTasks(patientId, shiftStart, medications = []) {
  const tasks = [];

  const vitalHours = [0, 2, 4, 8];
  vitalHours.forEach((h) => {
    tasks.push(createVitalsTask(patientId, addHours(shiftStart, h), 30, 30, h < 4 ? 'high' : 'normal'));
  });

  const noteHours = [0, 4, 8];
  noteHours.forEach((h) => {
    tasks.push(createProgressNoteTask(patientId, addHours(shiftStart, h), 30, 60));
  });

  tasks.push(...seedMedicationTasks(patientId, medications, shiftStart, [0, 4, 8], 20, 40));

  tasks.push(createManualTask(patientId, 'Wound Site Assessment', 0, shiftStart, 'high'));
  tasks.push(createManualTask(patientId, 'Wound Site Assessment', 6, shiftStart, 'normal'));
  tasks.push(createManualTask(patientId, 'Pain Assessment', 0, shiftStart, 'high'));
  tasks.push(createManualTask(patientId, 'Pain Assessment', 4, shiftStart, 'normal'));
  tasks.push(createManualTask(patientId, 'Pain Assessment', 8, shiftStart, 'normal'));
  tasks.push(createManualTask(patientId, 'Drain Output Check', 0, shiftStart, 'high'));
  tasks.push(createManualTask(patientId, 'Drain Output Check', 4, shiftStart, 'normal'));
  tasks.push(createManualTask(patientId, 'Drain Output Check', 8, shiftStart, 'normal'));

  tasks.push(createHandoverTask(patientId, shiftStart));

  return tasks;
}

function seedPediatricsTasks(patientId, shiftStart, medications = []) {
  const tasks = [];

  const vitalHours = [0, 3, 6, 9];
  vitalHours.forEach((h) => {
    tasks.push(createVitalsTask(patientId, addHours(shiftStart, h), 30, 45));
  });

  const noteHours = [0, 6];
  noteHours.forEach((h) => {
    tasks.push(createProgressNoteTask(patientId, addHours(shiftStart, h), 30, 60));
  });

  tasks.push(...seedMedicationTasks(patientId, medications, shiftStart, [0, 4, 8], 20, 30, 'high'));

  tasks.push(createManualTask(patientId, 'Growth and Development Check', 0, shiftStart));
  tasks.push(createManualTask(patientId, 'Pain Assessment (FLACC/Wong-Baker)', 0, shiftStart, 'high'));
  tasks.push(createManualTask(patientId, 'Pain Assessment (FLACC/Wong-Baker)', 6, shiftStart, 'normal'));
  tasks.push(createManualTask(patientId, 'Parent/Guardian Update', 4, shiftStart));

  tasks.push(createHandoverTask(patientId, shiftStart));

  return tasks;
}

function seedMaternityTasks(patientId, shiftStart, medications = []) {
  const tasks = [];

  const vitalHours = [0, 4, 8];
  vitalHours.forEach((h) => {
    tasks.push(createVitalsTask(patientId, addHours(shiftStart, h), 30, 60));
  });

  const noteHours = [0, 8];
  noteHours.forEach((h) => {
    tasks.push(createProgressNoteTask(patientId, addHours(shiftStart, h), 30, 60));
  });

  tasks.push(...seedMedicationTasks(patientId, medications, shiftStart, [0, 6], 30, 60));

  tasks.push(createManualTask(patientId, 'Fetal Heart Rate Monitoring', 0, shiftStart, 'high'));
  tasks.push(createManualTask(patientId, 'Fetal Heart Rate Monitoring', 4, shiftStart, 'high'));
  tasks.push(createManualTask(patientId, 'Fetal Heart Rate Monitoring', 8, shiftStart, 'high'));
  tasks.push(createManualTask(patientId, 'Fundal Height Assessment', 0, shiftStart));
  tasks.push(createManualTask(patientId, 'Fundal Height Assessment', 6, shiftStart));
  tasks.push(createManualTask(patientId, 'Lochia Assessment', 0, shiftStart));
  tasks.push(createManualTask(patientId, 'Lochia Assessment', 6, shiftStart));
  tasks.push(createManualTask(patientId, 'Breastfeeding Support', 2, shiftStart));
  tasks.push(createManualTask(patientId, 'Breastfeeding Support', 6, shiftStart));
  tasks.push(createManualTask(patientId, 'Breastfeeding Support', 10, shiftStart));
  tasks.push(createManualTask(patientId, 'Newborn Assessment', 0, shiftStart, 'high'));
  tasks.push(createManualTask(patientId, 'Newborn Assessment', 8, shiftStart, 'normal'));

  tasks.push(createHandoverTask(patientId, shiftStart));

  return tasks;
}

function dedupeScheduleTasks(tasks, shiftStart) {
  const keptBySignature = new Map();
  const passthrough = [];

  (tasks || []).forEach((task) => {
    if (task.type === 'generated' || task.type === 'fluid_balance') {
      passthrough.push(task);
      return;
    }

    const normalizedTask = normalizeSeededTask(task);
    const signature = getTaskSeedSignature(normalizedTask, shiftStart);
    const existing = keptBySignature.get(signature);
    if (!existing) {
      keptBySignature.set(signature, normalizedTask);
      return;
    }

    const existingComplete = existing.status === 'complete';
    const taskComplete = normalizedTask.status === 'complete';
    if (existingComplete && !taskComplete) {
      keptBySignature.set(signature, normalizedTask);
    }
  });

  return [...passthrough, ...keptBySignature.values()];
}

function mergePatientTasks(existingTasks, patient, shiftStartTime = new Date()) {
  const existing = Array.isArray(existingTasks) ? existingTasks : [];
  const shiftAnchor = inferShiftAnchorFromTasks(existing, shiftStartTime);
  const patientWithWard = {
    ...patient,
    wardType: resolvePatientWardType(patient),
  };
  const templateTasks = seedPatientTasks(patientWithWard, shiftAnchor);
  const dedupedExisting = dedupeScheduleTasks(existing, shiftAnchor);

  if (!dedupedExisting.length) return templateTasks;

  const missing = templateTasks.filter(
    (template) => !dedupedExisting.some((task) => tasksEquivalent(task, template, shiftAnchor))
  );

  return dedupeScheduleTasks([...dedupedExisting, ...missing], shiftAnchor);
}

function seedPatientTasks(patient, shiftStartTime) {
  const tasks = [];
  const shiftStart = new Date(shiftStartTime);
  const wardType = resolvePatientWardType(patient);
  const medications = patient?.medications || [];

  switch (wardType) {
    case 'icu':
      tasks.push(...seedICUTasks(patient.id, shiftStart, medications));
      break;
    case 'medical':
      tasks.push(...seedMedicalTasks(patient.id, shiftStart, medications));
      break;
    case 'surgical':
      tasks.push(...seedSurgicalTasks(patient.id, shiftStart, medications));
      break;
    case 'pediatrics':
      tasks.push(...seedPediatricsTasks(patient.id, shiftStart, medications));
      break;
    case 'maternity':
      tasks.push(...seedMaternityTasks(patient.id, shiftStart, medications));
      break;
    default:
      tasks.push(...seedAllWardsTasks(patient.id, shiftStart, medications));
  }

  tasks.push(createFluidBalanceTask(patient.id, shiftStart));
  return tasks;
}

export {
  seedPatientTasks,
  mergePatientTasks,
  dedupeScheduleTasks,
  seedAllWardsTasks,
  seedICUTasks,
  seedMedicalTasks,
  seedSurgicalTasks,
  seedPediatricsTasks,
  seedMaternityTasks,
  createFluidBalanceTask,
};
